import React, { useEffect, useState, useMemo } from "react";
import { Table, Card, Col, Row, Statistic, Spin, Input } from 'antd';
import {
  PlayCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useAuth } from "react-oidc-context";
import { GetOnboardedCampaign } from "../services/ApiService";
import {
  GetCampaignStatus,
  GenerateCampaignDeskUrl,
} from "../services/UtilService";
import Alert from "react-bootstrap/Alert";
const CampaignListUI = () => {
  const [data, setData] = useState([]);
  const [numOfEndedCampaigns, setNumOfEndedCampaigns] = useState([]);
  const [numOfLiveCampaigns, setNumOfLiveCampaigns] = useState([]);
  const [numOfUpcomingCampaigns, setNumOfUpcomingCampaigns] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [numOfCountries, setNumOfCountries] = useState([]);
  const [numOfPartners, setNumOfPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = useAuth();
  const [showError, setShowError] = useState(false);
  const columns = useMemo(
    () => [
      {
        dataIndex: "campaign_id",
        key: "cid",
        title: "Campaign Id",
        render: (text, record) => (
          <a
            href={GenerateCampaignDeskUrl(
              record.campaign_id,
              record.partner_id,
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            {record.campaign_id}
          </a>
        ),
      },
      {
        dataIndex: "advertiser_id",
        key: "aid",
        title: "Advertiser Id",
        sorter: (a, b) => a.advertiser_id.localeCompare(b.advertiser_id),
      },
      {
        dataIndex: "partner_id",
        key: "partner_id",
        title: "Partner Id",
        sorter: (a, b) => a.partner_id.localeCompare(b.partner_id),
      },
      {
        dataIndex: "channel",
        key: "channel",
        title: "Channel",
        sorter: (a, b) => a.channel.localeCompare(b.channel),
      },
      {
        dataIndex: "tccc_ou",
        key: "tccc_ou",
        title: "TCCC OU",
        sorter: (a, b) => a.tccc_ou.localeCompare(b.tccc_ou),
      },
      {
        dataIndex: "tccc_country",
        key: "tccc_country",
        title: "TCCC Country",
        sorter: (a, b) => a.tccc_country.localeCompare(b.tccc_country),
      },
      {
        dataIndex: "start_date",
        key: "start_date",
        title: "Start Date",
        sorter: (a, b) => a.start_date.localeCompare(b.start_date),
      },
      {
        dataIndex: "end_date",
        key: "end_date",
        title: "End Date",
        sorter: (a, b) => a.end_date.localeCompare(b.end_date),
      },
      {
        dataIndex: "campaign_status",
        key: "campaign_status",
        title: "Campaign Status",
        render: (text, record) => {
          const color = record.campaign_status_color;
          return <span style={{ color }}>{text}</span>;
        },
        sorter: (a, b) => a.campaign_status.localeCompare(b.campaign_status),
      },
      {
        dataIndex: "utc_timestamp_upload",
        key: "utc_timestamp_upload",
        title: "Uploaded Timestampe(UTC)",
        sorter: (a, b) =>
          a.utc_timestamp_upload.localeCompare(b.utc_timestamp_upload),
      },
      {
        dataIndex: "initial_bidlist_association",
        key: "initial_bidlist_association",
        title: "Bidlist Association(Initial)",
        sorter: (a, b) =>
          a.initial_bidlist_association.localeCompare(
            b.initial_bidlist_association,
          ),
      },
      {
        dataIndex: "latest_bidlist_association",
        key: "latest_bidlist_association",
        title: "Bidlist Association(Latest)",
        sorter: (a, b) =>
          a.latest_bidlist_association.localeCompare(
            b.latest_bidlist_association,
          ),
      },
    ],
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const raw_data = await GetOnboardedCampaign(auth.user.id_token);
        console.log(raw_data);

        const data = raw_data.map((campaign) => {
          const { status, color } = GetCampaignStatus(
            campaign.start_date,
            campaign.end_date,
          );
          return {
            ...campaign,
            campaign_status: status,
            campaign_status_color: color,
          };
        });
        const uniquePartnerIds = new Set(data.map(item => item.partner_id));
        const uniqueCountries = new Set(data.map(item => item.tccc_country));
        const endedCampaigns = data.filter(item => item.campaign_status==="Ended");
        const liveCampaigns = data.filter(item => item.campaign_status==="Live");
        const upcomingCampaigns = data.filter(item => item.campaign_status==="Upcoming");

        setData(data);
        setLoading(false);
        setNumOfEndedCampaigns(endedCampaigns.length)
        setNumOfLiveCampaigns(liveCampaigns.length)
        setNumOfUpcomingCampaigns(upcomingCampaigns.length)
        setNumOfPartners(uniquePartnerIds.size)
        setNumOfCountries(uniqueCountries.size)

      } catch (err) {
        console.log(err.message);
        setError("Failed to retrieve the onboarded campaigns");
        setLoading(false);
        setShowError(true);
      }
    };
    fetchData();
  }, [auth.user.id_token]);

  // Match the campaign id by case-insensitive substring.
  const searchTerm = searchText.trim().toLowerCase();
  const filteredData = data.filter(
    (item) =>
      (!statusFilter || item.campaign_status === statusFilter) &&
      (!searchTerm || item.campaign_id.toLowerCase().includes(searchTerm)),
  );

  // Tiles with a `status` filter the table when clicked; click again to clear.
  const stats = [
    { title: "Live", status: "Live", value: numOfLiveCampaigns, color: "#3f8600", icon: <PlayCircleOutlined /> },
    { title: "Upcoming", status: "Upcoming", value: numOfUpcomingCampaigns, color: "#1677ff", icon: <ClockCircleOutlined /> },
    { title: "Ended", status: "Ended", value: numOfEndedCampaigns, color: "#cf1322", icon: <CheckCircleOutlined /> },
    { title: "Partners", value: numOfPartners, color: "#722ed1", icon: <TeamOutlined /> },
    { title: "Countries", value: numOfCountries, color: "#fa8c16", icon: <GlobalOutlined /> },
  ];

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><Spin size="large" /></div>;

  return (
    <div className="table-container">
      {showError && (
        <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
          <Alert.Heading>Error!</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      <Row gutter={[16, 16]} justify="center">
        {stats.map((stat) => (
          <Col key={stat.title} xs={12} md={8} xl={4}>
            <Card
              hoverable={!!stat.status}
              bordered={false}
              onClick={
                stat.status
                  ? () => setStatusFilter(statusFilter === stat.status ? null : stat.status)
                  : undefined
              }
              style={{
                borderTop: `4px solid ${stat.color}`,
                boxShadow:
                  statusFilter && statusFilter === stat.status
                    ? `0 0 0 2px ${stat.color}, 0 4px 12px rgba(0, 0, 0, 0.15)`
                    : "0 2px 8px rgba(0, 0, 0, 0.08)",
                cursor: stat.status ? "pointer" : "default",
              }}
              styles={{ body: { padding: 20 } }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Statistic
                  title={stat.title}
                  value={stat.value}
                  valueStyle={{ color: stat.color, fontWeight: 600 }}
                />
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    color: stat.color,
                    background: `${stat.color}1A`,
                  }}
                >
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <Input.Search
        placeholder="Filter by Campaign Id"
        allowClear
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ margin: "16px 0", maxWidth: 480 }}
      />
      <Table dataSource={filteredData} columns={columns} rowKey="campaign_id" pagination={{ pageSize: 10, showSizeChanger: true }} />
    </div>
  );
};

export default CampaignListUI;
